/**
 * ItemFormTabs — single unified card: tab bar ▸ tab content ▸ action footer.
 *
 * Props:
 *   tabIndex        number
 *   onTabChange     (index: number) => void
 *   isEdit          boolean — tabs 3-5 disabled until item is saved
 *   maxUnlockedTab  number  — on new item, wizard tabs ahead of this are locked
 *   completionStatus  array[6] — 'complete'|'warning'|'error'|'empty'
 *   actions         ReactNode — Cancel / Save buttons rendered in the footer bar
 *   children        array[6]  — one node per tab panel
 */
import React from 'react';
import { Box, Tabs, Tab, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import CancelIcon         from '@mui/icons-material/Cancel';
import { IPD_COLORS, cardSx } from '../../theme';

const TAB_LABELS = [
  'Identity', 'Taxation', 'Clinical', 'UOM Conversion',
  'Barcodes', 'Costing',
];

/** Tabs that need a persisted item id before they are usable */
const REQUIRES_SAVE = new Set([3, 4, 5]);
const WIZARD_LAST_TAB = 2;

function StatusIcon({ status }) {
  if (status === 'complete') return <CheckCircleIcon  sx={{ fontSize: 13, color: IPD_COLORS.success.text,  ml: 0.5 }} />;
  if (status === 'warning')  return <WarningAmberIcon sx={{ fontSize: 13, color: IPD_COLORS.warning.text, ml: 0.5 }} />;
  if (status === 'error')    return <CancelIcon       sx={{ fontSize: 13, color: IPD_COLORS.error.text,   ml: 0.5 }} />;
  return null;
}

function TabLabel({ label, status }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{label}</Typography>
      <StatusIcon status={status} />
    </Box>
  );
}

function TabPanel({ children, tabIndex, index }) {
  return (
    <Box
      role="tabpanel"
      id={`item-tabpanel-${index}`}
      aria-labelledby={`item-tab-${index}`}
      hidden={tabIndex !== index}
    >
      {tabIndex === index && children}
    </Box>
  );
}

export default function ItemFormTabs({
  tabIndex,
  onTabChange,
  isEdit,
  maxUnlockedTab = 5,
  completionStatus = Array(6).fill('empty'),
  actions,
  children,
}) {
  return (
    <Box sx={{ ...cardSx, borderRadius: '12px', overflow: 'hidden' }}>

      {/* ── Tab bar ─────────────────────────────────────────────── */}
      <Box sx={{
        px: 1,
        borderBottom: `1px solid ${IPD_COLORS.border}`,
        backgroundColor: IPD_COLORS.cardBg,
      }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => onTabChange(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTabs-indicator': { backgroundColor: IPD_COLORS.primary },
            '& .MuiTab-root': {
              color: IPD_COLORS.textSecondary,
              textTransform: 'none',
              minHeight: 44,
              fontSize: 13,
              fontWeight: 500,
              px: 2,
              '&.Mui-selected': { color: IPD_COLORS.primary, fontWeight: 600 },
              '&.Mui-disabled': { opacity: 0.45 },
            },
          }}
        >
          {TAB_LABELS.map((label, i) => {
            const requiresSave = !isEdit && REQUIRES_SAVE.has(i);
            const wizardLocked = !isEdit && i <= WIZARD_LAST_TAB && i > maxUnlockedTab;
            const disabled = requiresSave || wizardLocked;
            const tab = (
              <Tab
                key={label}
                id={`item-tab-${i}`}
                aria-controls={`item-tabpanel-${i}`}
                disabled={disabled}
                label={<TabLabel label={label} status={completionStatus[i]} />}
              />
            );
            if (disabled) {
              const title = requiresSave
                ? 'Create the item first to enable this section'
                : 'Complete the previous step first';
              return (
                <Tooltip key={label} title={title} placement="bottom">
                  <span>{tab}</span>
                </Tooltip>
              );
            }
            return tab;
          })}
        </Tabs>
      </Box>

      {/* ── Tab panels ──────────────────────────────────────────── */}
      <Box sx={{ p: 2.5 }}>
        {React.Children.map(children, (child, i) => (
          <TabPanel key={i} tabIndex={tabIndex} index={i}>
            {child}
          </TabPanel>
        ))}
      </Box>

      {/* ── Action footer ────────────────────────────────────────── */}
      {actions && (
        <Box sx={{
          borderTop: `1px solid ${IPD_COLORS.border}`,
          px: 2.5,
          py: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          backgroundColor: IPD_COLORS.cardBg,
        }}>
          {actions}
        </Box>
      )}

    </Box>
  );
}
